/**
 * Triggers our collapsible and sets up various change detectors
 * 
 * @param {String} collapsibleId ID of the collapsible we want to toggle
 * 
 * @returns {Promise<void>}
 */
async function toggleCollapsible(collapsibleId) {
    const collapsible = document.getElementById(collapsibleId);
    if (!collapsible) return;
    
    const updateMaxHeight = async () => {
        const currentTransition = collapsible.style.transition;
        collapsible.style.transition = "none";
        await new Promise(r => requestAnimationFrame(r));
        collapsible.style.maxHeight = `${collapsible.scrollHeight}px`;
        await new Promise(r => requestAnimationFrame(r));
        collapsible.style.transition = currentTransition;
    };

    await updateMaxHeight();

    collapsible.classList.toggle('collapsed');

    // Set up ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(async entries => {
        for (let entry of entries) {
            if (entry.target === collapsible && !collapsible.classList.contains('collapsed')) {
                await updateMaxHeight();
            }
        }
    });

    // Start observing the collapsible element for size changes
    resizeObserver.observe(collapsible);

    // CSS change events to listen for
    const cssEvents = ['mouseenter', 'mouseleave', 'focus', 'blur', 'transitionend'];

    // Add listeners for CSS-related events
    cssEvents.forEach(eventType => {
        collapsible.addEventListener(eventType, async () => {
            await updateMaxHeight();
        });
    });

    // Set up MutationObserver to watch for attribute changes
    const mutationObserver = new MutationObserver(async mutations => {
        await updateMaxHeight();
    });

    // Start observing the collapsible element for attribute changes
    mutationObserver.observe(collapsible, { attributes: true, attributeFilter: ['style', 'class'] });

    // Listen for window resize events
    window.addEventListener('resize', async () => {
        await updateMaxHeight();
    });
}

/**
 * 
 * Saca el contenido de nuestro elemento y lo muestra en la barra lateral
 * 
 * @param {String}             elementID      ID del elemento que queremos mostrar
 * @param {String|HTMLElement} sidePopupTitle Título del popup
 * @param {String}             side           Lado del que sale el popup
 * @param {Bool}               animation      Si quieres animación o no
 * 
 * @return {Void}
 * 
 */
function displayElementOnSideBar(elementID, sidePopupTitle, side = "left", animation = true) {
    let documentContent = document.querySelector(`#${elementID}`);
    if (!documentContent) {
        documentContent = createNode("div", ["error"], {innerHTML: `404 Element NOT FOUND`});
    } else {
        let newDisplay = "flex";
        if (documentContent?.dataset?.customDisplay) {
            newDisplay = documentContent.dataset.customDisplay;
        }
        documentContent.style.display = newDisplay;
    }
    showSidePopup(`side-popup-${elementID}`, documentContent, sidePopupTitle, side, animation);
}

/**
 * 
 * Show a popup
 * 
 * @param {String}             id        ID para el popover para evitar duplicados en caso de múltiples clicks
 * @param {HTMLElement}        content   El contenido a renderizar dentro del sidepopover
 * @param {String|HTMLElement} title     El título para el popover
 * @param {String}             side      El lado del que quieres que salga
 * @param {Bool}               animation Si quieres animación o no
 * 
 * @return {Void} 
 *  
 */
function showSidePopup(id, content, title, side = "left", animation = true) {
    let currentPopup = document.querySelector(`#${id}`);
    let sidePopups = document.querySelectorAll(".side-popup");
    let overlay = document.querySelector('.popup-overlay');

    if (!overlay) {
        overlay = createNode("div", ["popup-overlay"]);
        overlay.onclick = () => closeAllPopups();
        document.body.appendChild(overlay);
    }

    if (!currentPopup) {  
        let sidePopup  = createNode("div", ["side-popup"], {id: id});
        let closePopup = () => {
            sidePopup.style[`${side}`] = `-${sidePopup.clientWidth}px`;
            overlay.style.display = 'none'; // Ocultar el fondo
        };
        sidePopup.close = closePopup;

        let sidePopupHeader = createNode("div", ["side-popup-header"], {});
        let sidePopupTitle  = createNode("div", ["side-popup-header-title"], {innerHTML: `ERR`});
        sidePopupTitle.style.color = 'black';
        sidePopupTitle.style.fontSize = '14px';
        if (typeof title === "string") {
            if (title == "") {
                title = `&nbsp;`;
            }
            sidePopupTitle = createNode("div", ["side-popup-header-title"], {innerHTML: title});
            sidePopupTitle.style.color = 'black';
            sidePopupTitle.style.fontSize = '14px';

        } else {
            sidePopupTitle = title;
        }
        sidePopupHeader.appendChild(sidePopupTitle);
        let sidePopupClose = createNode("div", ["side-popup-header-close"], {innerHTML: `CLOSE`, onclick: closePopup});
        sidePopupHeader.style.fontSize = '14px';
        sidePopupHeader.appendChild(sidePopupClose);
        sidePopup.appendChild(sidePopupHeader);

        let sidePopupBody = createNode("div", ["side-popup-body"]);
        sidePopupBody.appendChild(content);
        sidePopup.appendChild(sidePopupBody);
        document.body.appendChild(sidePopup);
        sidePopup.style[`${side}`] = `-${sidePopup.clientWidth}px`;
        currentPopup = sidePopup; 
    } 

    currentPopup.classList = `side-popup side-popup-${side} animation-${animation}`;

    if (id == "side-popup-size-chart") {
        currentPopup.classList = `side-popup side-popup-${side} animation-${animation} custom-width-popup`;
    }

    setTimeout(() => {
        if (id == "side-popup-market-selector") {
            currentPopup.style.bottom = "0px";
        }
        currentPopup.style[`${side}`] = "0px";
        overlay.style.display = 'block';
    }, (animation ? 10 : 0));
    
    sidePopups.forEach(sidePopup => {
        sidePopup.close();
    });
}

function getCartHeader(activeHeader) {
    let _cartHeader = document.createElement("div");
    _cartHeader.classList.add("cart-header-switcher-wrapper");
    _cartHeader.style.fontSize = "14px";

    // Crear el botón BAG
    let cartHeaderCart = document.createElement("div");
    cartHeaderCart.id = "cart-header-cart";
    cartHeaderCart.classList.add("cart-header-switcher");

    if (activeHeader == "bag") {
        cartHeaderCart.classList.add("active");
        cartHeaderCart.style.color = "black";
    }
    cartHeaderCart.onmouseover = () => {
        if (!cartHeaderCart.classList.contains("active")) {
            cartHeaderCart.classList.add("hover");
        }
    };
    
    cartHeaderCart.onmouseout = () => {
        if (!cartHeaderCart.classList.contains("active")) {
            cartHeaderCart.classList.remove("hover");
        }
    };
    cartHeaderCart.onclick = () => {
        displayElementOnSideBar("custom-cart", getCartHeader("bag"), "right", false);
    };
    cartHeaderCart.innerHTML = `BAG (${document.querySelector("custom-cart").itemCount})`;

    window.addEventListener("cart-item-count-updated", ({ detail: itemCount }) => {
        cartHeaderCart.innerHTML = `BAG (${itemCount})`;
    });

    _cartHeader.appendChild(cartHeaderCart);

    // Crear el botón WISHLIST
    let cartHeaderWishlist = document.createElement(`div`);
    cartHeaderWishlist.id = `cart-header-wishlist`;
    cartHeaderWishlist.classList.add(`cart-header-switcher`);
    if (activeHeader === `wishlist`) {
        cartHeaderWishlist.classList.add(`active`);
    }
    cartHeaderWishlist.onmouseover = () => {
        if (activeHeader !== `wishlist`) { // Solo aplicar hover si no es el activo
            cartHeaderWishlist.style.color = "black";
        }
    };
    cartHeaderWishlist.onmouseout = () => {
        if (activeHeader !== `wishlist`) {
            cartHeaderWishlist.style.color = ""; // Restaurar color original
        }
    };
    cartHeaderWishlist.onclick = () => {
        displayElementOnSideBar(`custom-wishlist`, getCartHeader(`wishlist`), `right`, false);
    };
    cartHeaderWishlist.innerHTML = `WISHLIST`;

    _cartHeader.appendChild(cartHeaderWishlist);

    return _cartHeader;
}


function closeAllPopups() {
    let sidePopups = document.querySelectorAll(".side-popup");
    sidePopups.forEach(sidePopup => {
        sidePopup.close();
    });
    let overlay = document.querySelector('.popup-overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
}

/**
 * 
 * @param {HTMLElement} input     
 * @param {String}      errorMessage 
 * @param {Boolean}     removeOnInput
 * 
 */
function customInputError(input, errorMessage, removeOnInput = true) {
    input.classList.add("has-error");
    let errorNode = document.createElement("div");
        errorNode.classList.add(`custom-error-field`);
        errorNode.innerHTML = errorMessage;
    
    let currentErrorNode = input.parentElement.querySelector(`.custom-error-field`);
    if (removeOnInput) {
        input.addEventListener("input", () => {
            input.parentElement.querySelector(`.custom-error-field`).remove();
            input.classList.remove("has-error");
        }, {once: true});
    }
    if (currentErrorNode) {
        currentErrorNode.addEventListener("animationend", () => {
            currentErrorNode.classList.remove("remind-error");
        }, {once: true});
        currentErrorNode.classList.add("remind-error");
    } else {
        input.parentElement.appendChild(errorNode);
    }
}